import { WbTextField } from '@agilelab/plugin-wb-platform';
import { TextFieldProps } from '@material-ui/core/TextField';
import { WidgetProps } from '@rjsf/utils';
import { ChangeEvent, FocusEvent, useMemo, useEffect } from 'react';
import { useFormExtraConfigContext } from '../../../contexts/FormExtraConfigContext';
import { extractCustomProperties, isHidden } from '../../utils';
import { UseDependencyManagerResult } from '../DependencyManager';
import { extractFieldPathFromRjsfId } from '../utils/extractFieldPathFromRjsfId';

export function dateToString(value: any): string {
  if (!value) return '';

  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return value.split('T')[0];
    }
  }

  return '';
}

export const DateWidget = ({
  id,
  placeholder,
  required,
  readonly,
  disabled,
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  options,
  schema,
  uiSchema,
  rawErrors = [],
  formContext,
  registry,
  ...textFieldProps
}: WidgetProps) => {
  const customProps = uiSchema ? extractCustomProperties(uiSchema) : {};
  const ariaLabel = label || id;

  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const rootSchema = formContext?.rootSchema;

  const fieldPath = useMemo(() => {
    return extractFieldPathFromRjsfId(id, rootSchema);
  }, [id, rootSchema]);

  // Value used for display only (input value prop). This never mutates
  // upstream form state on its own.
  const stringValue = dateToString(value);

  // If the incoming value is a full ISO datetime (e.g. a schema default,
  // data coming from an API, or a dependency writing an unnormalized
  // value), normalize the upstream form state to YYYY-MM-DD.
  // This is NOT for display (that's already handled by dateToString /
  // stringValue above) — it's to make sure the form state itself never
  // holds a datetime for what is a plain date field. Without this,
  // the input would *look* correct but any downstream logic comparing
  // date strings (dependencies, validation, etc.) would break.
  useEffect(() => {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const normalizedValue = dateToString(value);

      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value: normalizedValue,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(normalizedValue);
      }
    }
  }, [value, onChange, dependencyManager, fieldPath]);

  const _onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (dependencyManager?.notifyUpdate && fieldPath) {
      dependencyManager.notifyUpdate({
        fieldPath,
        value: newValue,
        source: 'user',
        intent: 'manual',
      });
    } else {
      onChange(newValue);
    }
  };

  const _onBlur = (event: FocusEvent<HTMLInputElement>) =>
    onBlur(id, event.target.value);
  const _onFocus = (event: FocusEvent<HTMLInputElement>) =>
    onFocus(id, event.target.value);

  const { debounceMs } = useFormExtraConfigContext();

  return (
    <WbTextField
      id={id}
      name={id}
      placeholder={placeholder}
      label={schema.title}
      style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
      required={required}
      disabled={disabled || readonly}
      type="date"
      inputProps={{
        ...(!schema.title ? { 'aria-label': ariaLabel } : {}),
      }}
      InputLabelProps={{
        shrink: true,
      }}
      value={stringValue}
      error={rawErrors.length > 0}
      debounceMs={debounceMs}
      onChange={_onChange}
      onBlur={_onBlur}
      onFocus={_onFocus}
      {...(textFieldProps as TextFieldProps)}
      {...customProps}
    />
  );
};
